import { AnswerType } from 'components/modals/QuestionList/QuestionList';
import LocationEpisode from 'models/LocationEpisode';
import { QuestionKind, QuestionType } from 'models/QuestionTemplate';

export enum DischargeSteps {
  Information = 'information',
  FileRequests = 'fileRequests',
  Questions = 'questions',
  Confirm = 'confirm',
}

function removeStep(steps: DischargeSteps[], step: DischargeSteps) {
  const stepIndex = steps.findIndex((s) => s === step);
  steps.splice(stepIndex, 1);
}

export function buildDischargeFormSteps({
  hasDischargeQuestions = false,
  canContinueCare = false,
  hasAllRequestedFiles = false,
  hideDischargeSummaryPrompt = false,
  currentStep,
}) {
  const allSteps = Object.values(DischargeSteps);

  if (hideDischargeSummaryPrompt || (hasAllRequestedFiles && currentStep !== DischargeSteps.FileRequests)) {
    removeStep(allSteps, DischargeSteps.FileRequests);
  }

  if (!hasDischargeQuestions) {
    removeStep(allSteps, DischargeSteps.Questions);
  }

  if (!canContinueCare) {
    removeStep(allSteps, DischargeSteps.Confirm);
  }

  return allSteps;
}

export function buildAnsweredQuestionsFromFormValues(
  questions: QuestionType[],
  answers: Record<string, string>
): AnswerType[] {
  return questions.reduce((acc: AnswerType[], question) => {
    const answer = answers[question.id] ?? null; // this will retain the answer if it's an empty string

    if (answer === null) return acc;

    const answerWeight = findAnswerWeight(question, answer);

    acc.push({
      ...question,
      answer: {
        text: answer,
        weight: answerWeight,
      },
    });

    return acc;
  }, []);
}

// A question is optional when explicitly configured as such, OR when it's a notes
// question with requiredRatings and none of the satisfaction-scale answers match
// any of those ratings (so the notes prompt shouldn't be required).
export function isQuestionOptional(
  question: QuestionType,
  allQuestions: QuestionType[],
  answers: Record<string, string> | null | undefined
): boolean {
  if (question.config?.optional) return true;

  const satisfactionScaleQuestions = allQuestions.filter((q) => q.kind === QuestionKind.SATISFACTION_SCALE);
  if (
    satisfactionScaleQuestions.length &&
    question.kind === QuestionKind.NOTES &&
    question.config?.requiredRatings?.length
  ) {
    const satisfactionValues = satisfactionScaleQuestions.map((q) => answers?.[q.id]);
    return question.config.requiredRatings.every((rating) =>
      satisfactionValues.every((value) => value !== `${rating} out of 5`)
    );
  }

  return false;
}

function findAnswerWeight(question: QuestionType, answer: string) {
  if (question.kind === QuestionKind.YES_NO) {
    return question.config.positive?.toLowerCase() === answer.toLowerCase() ? 'positive' : 'negative';
  }

  return 'n/a';
}

export function buildLinkedDischargeFormSteps({
  dischargeQuestionLEs,
  canContinueCare,
  hasAllRequestedFiles,
  hideDischargeSummaryPrompt,
  currentStep,
}: {
  dischargeQuestionLEs: LocationEpisode[];
  canContinueCare: boolean;
  hasAllRequestedFiles: boolean;
  hideDischargeSummaryPrompt: boolean;
  currentStep: string;
}): string[] {
  const steps: string[] = [DischargeSteps.Information];

  if (!hideDischargeSummaryPrompt && !(hasAllRequestedFiles && currentStep !== DischargeSteps.FileRequests)) {
    steps.push(DischargeSteps.FileRequests);
  }

  for (const le of dischargeQuestionLEs) {
    steps.push(`questions_${le.id}`);
  }

  if (canContinueCare) {
    steps.push(DischargeSteps.Confirm);
  }

  return steps;
}
